import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageShell, Skeleton, TemporaryErrorPanel } from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { returnStateFor } from '@/features/auth/model/return-path';
import { restoreSession } from '@/features/auth/store/auth.slice';
import { selectAuth } from '@/features/auth/store/auth.selectors';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';

/**
 * PANEL KAPISI — FAIL-CLOSED.
 *
 * BU KAPI GÜVENLİK SINIRI DEĞİLDİR. İstemci paketi kullanıcının makinesindedir;
 * onu değiştiren biri buradaki koşulu da değiştirebilir. Gerçek sınır API'dedir
 * (`StaffJwtGuard` + `StaffPermissionsGuard`, ayrı imza anahtarı + ayrı
 * audience): personel jetonu olmayan hiçbir istek `/panel/*` altından veri
 * alamaz.
 *
 * KAPI ÜÇ DURUMU AYIRIR (eskiden ALTI vardı):
 *  1. KARAR YOK (`bootstrapped` değil) → hiçbir içerik çizilmez, iskelet.
 *  2. KARAR VERİLEMEDİ (429/5xx/ağ) → geçici hata + tekrar dene. Personel
 *     girişe ATILMAZ: jeton çerezde canlı olabilir (17B+17E dersi).
 *  3. OTURUM YOK → giriş ekranına yönlendirilir. Bulunulan adres gezinme
 *     durumunda taşınır; girişten sonra personel kaldığı ekrana döner
 *     (oturum süresi doldu, B-02).
 *
 * ÜÇ DURUM NEDEN KAYBOLDU (panel kimliği ayrımı · Karar 1):
 *  · "rol panele giremiyor" → `staff_accounts`ta satırı olan HERKES panele
 *    girer; rol yalnız hangi ekranın veri döndüreceğini belirler.
 *  · "şifre kurulmamış" → şifresiz personel giriş bile YAPAMAZ; ilk şifre
 *    davet jetonuyla `/kurulum` ekranında kurulur.
 *  · "bu oturum panel yolundan gelmemiş" → panel oturumu ayrı tabloda;
 *    `staff_sessions`ta satır olmak panel yolundan gelmek demektir.
 * Üçü de bir ÇALIŞMA ZAMANI SORUSUYDU ve üçü de artık YAPISAL olgudur.
 */
export function StaffGate({ children }: { children: ReactNode }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectAuth);
  const location = useLocation();

  if (!auth.bootstrapped) {
    return (
      <PageShell rail="reading" as="main">
        <Skeleton label={t.t('admin.panel.loading')} lines={6} lineHeight={20} />
      </PageShell>
    );
  }

  if (auth.sessionUndecided && !auth.isAuthenticated) {
    return <TemporaryErrorPanel onRetry={() => dispatch(restoreSession.request())} />;
  }

  if (!auth.isAuthenticated) {
    return (
      <Navigate
        to={ROUTES.signIn}
        replace
        state={returnStateFor(location.pathname, location.search)}
      />
    );
  }

  return <>{children}</>;
}
